import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { apiBaseUrl } from '../../utils/Api/Api';
import ProductPriceDisplay from './ProductPriceDisplay';
import CartQuantityStepper from './CartQuantityStepper';
import ProductPhotoPlaceholder from '../../images/placeholder_product_image.png';
import AddIcon from '@mui/icons-material/Add';
import {
  layoutCardRadius,
  lead600,
  lead900,
  surfaceProduct,
} from '../../theme/colors';

const titleLayoutSx = {
  textAlign: 'left',
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
  display: 'block',
  maxHeight: '40px',
  whiteSpace: 'normal',
  position: 'relative',
  wordBreak: 'break-word',
};

const mainFontBody = {
  fontFamily: 'Nunito',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '21px',
  color: lead600,
};

const ProductCard = ({
  variant,
  product,
  detailMode,
  showCartControls = false,
  productIndex,
  shoppingCartProducts = [],
  onAdd,
  onIncrement,
  onDecrement,
  sx: sxProp,
}) => {
  const imageSrc = product.item_photo
    ? `${apiBaseUrl}${product.item_photo}`
    : ProductPhotoPlaceholder;

  const inCart = showCartControls && shoppingCartProducts.some((item) => item.id === product.id);
  const cartItem =
    showCartControls && productIndex !== undefined
      ? shoppingCartProducts.find((item) => item.productIndex === productIndex)
      : undefined;
  const cartQuantity = cartItem?.cartQuantity ?? 0;
  const atMaxStock =
    product.quantity > 0 && cartQuantity >= product.quantity;

  const rootSx =
    variant === 'gray'
      ? {
          bgcolor: surfaceProduct,
          borderRadius: layoutCardRadius,
          minHeight: '254px',
          display: 'flex',
          padding: '12px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2,
          height: '100%',
          overflow: 'hidden',
        }
      : {
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: layoutCardRadius,
          display: 'flex',
          padding: '12px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2,
          alignSelf: 'stretch',
          flexShrink: 0,
          overflow: 'hidden',
        };

  const detailNode =
    detailMode === 'description' ? (
      <Typography
        textAlign="left"
        sx={{
          ...mainFontBody,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          wordBreak: 'break-word',
          flex: 1,
        }}
      >
        {product.description}
      </Typography>
    ) : product.weight ? (
      <Typography
        textAlign="left"
        sx={{ ...mainFontBody, fontWeight: 400, color: lead900 }}
      >
        {product.weight}
      </Typography>
    ) : null;

  const stop = (e) => e.stopPropagation();

  const priceRow = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: showCartControls ? 'space-between' : 'flex-start',
        alignItems: 'center',
        alignSelf: 'stretch',
        minHeight: showCartControls ? '24px' : undefined,
      }}
    >
      <ProductPriceDisplay product={product} sx={{ flexShrink: 0 }} />

      {showCartControls && !inCart && (
        <Button
          variant="contained"
          color="secondary"
          onClick={(e) => {
            stop(e);
            onAdd?.(e);
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 0.5,
            gap: 1,
            borderRadius: layoutCardRadius,
            minWidth: '24px',
            width: '24px',
            height: '24px',
            justifyContent: 'center',
            flexShrink: 0,
            m: 0,
          }}
        >
          <AddIcon sx={{ fontSize: 20, color: '#fff' }} />
        </Button>
      )}

      {showCartControls && inCart && (
        <CartQuantityStepper
          cartQuantity={cartQuantity}
          onIncrement={onIncrement}
          onDecrement={onDecrement}
          disabledIncrement={atMaxStock}
          size={variant === 'white' ? 'md' : 'sm'}
        />
      )}
    </Box>
  );

  return (
    <Box sx={{ ...rootSx, ...sxProp }}>
      <Box
        component="img"
        src={imageSrc}
        alt={product.title}
        sx={{
          height: '128px',
          width: '100%',
          objectFit: 'cover',
          borderRadius: '16px',
          alignSelf: 'stretch',
        }}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1,
          alignSelf: 'stretch',
          flex: variant === 'gray' ? 1 : undefined,
        }}
      >
        <Typography
          variant="storeName"
          sx={{
            ...titleLayoutSx,
            fontWeight: 700,
            lineHeight: '20px',
            '&::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              bottom: 0,
              width: '40px',
              height: '20px',
              background: `linear-gradient(90deg, rgba(255,255,255,0), ${variant === 'gray' ? surfaceProduct : '#fff'})`,
              pointerEvents: 'none',
            },
          }}
        >
          {product.title}
        </Typography>

        {detailNode}

        {priceRow}
      </Box>
    </Box>
  );
};

export default ProductCard;
